import api from './api';

/**
 * Discount Service
 * Manages promotions and discount codes
 * Connected to Backend API
 */

class DiscountService {
    /**
     * Get all discounts
     * @returns {Promise<Array>}
     */
    async getDiscounts() {
        try {
            const response = await api.get('/admin/coupons');
            // Map backend fields to frontend expected format if needed
            return response.data.map(coupon => ({
                id: coupon.id,
                code: coupon.code,
                name: coupon.name || coupon.code, // Use name from backend, fallback to code
                type: coupon.discountPercent ? 'percentage' : 'fixed',
                value: coupon.discountPercent || coupon.discountAmount,
                target_type: 'all', // Default
                target_ids: [],
                start_date: coupon.validFrom,
                end_date: coupon.validTo,
                usage_limit: coupon.maxUses,
                usage_count: coupon.currentUses,
                is_active: Boolean(coupon.isActive), // Ensure boolean
            }));
        } catch (error) {
            console.error('Error fetching discounts:', error);
            return [];
        }
    }

    /**
     * Create a new discount
     * @param {Object} discountData
     * @returns {Promise<Object>}
     */
    async createDiscount(discountData) {
        try {
            const payload = {
                code: discountData.code,
                name: discountData.name,
                discountPercent: discountData.type === 'percentage' ? discountData.value : null,
                discountAmount: discountData.type === 'fixed' ? discountData.value : null,
                maxUses: discountData.usage_limit,
                validFrom: discountData.start_date || null,
                validTo: discountData.end_date || null,
                isActive: discountData.is_active
            };
            const response = await api.post('/admin/coupons', payload);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to create discount');
        }
    }

    /**
     * Update a discount
     * @param {number} id
     * @param {Object} discountData
     * @returns {Promise<Object>}
     */
    async updateDiscount(id, discountData) {
        try {
            const payload = {};
            if (discountData.code) payload.code = discountData.code;
            if (discountData.name) payload.name = discountData.name;
            if (discountData.type && discountData.value) {
                payload.discountPercent = discountData.type === 'percentage' ? discountData.value : null;
                payload.discountAmount = discountData.type === 'fixed' ? discountData.value : null;
            }
            if (discountData.usage_limit !== undefined) payload.maxUses = discountData.usage_limit;
            if (discountData.start_date !== undefined) payload.validFrom = discountData.start_date || null;
            if (discountData.end_date !== undefined) payload.validTo = discountData.end_date || null;
            if (discountData.is_active !== undefined) payload.isActive = discountData.is_active;

            const response = await api.patch(`/admin/coupons/${id}`, payload);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update discount');
        }
    }

    /**
     * Delete a discount
     * @param {number} id
     * @returns {Promise<boolean>}
     */
    async deleteDiscount(id) {
        try {
            await api.delete(`/admin/coupons/${id}`);
            return true;
        } catch (error) {
            console.error('Error deleting discount:', error);
            throw new Error(error.response?.data?.message || 'Failed to delete discount');
        }
    }

    /**
     * Toggle discount active status
     * @param {number} id
     * @returns {Promise<Object>}
     */
    async toggleDiscountStatus(id) {
        // We need to fetch the current status first or just send the inverted status if passed
        // For now, let's assume the component handles the state toggle and calls updateDiscount
        // But to keep signature compatible:
        // We can't easily toggle without knowing current state here unless we fetch.
        // Better to rely on updateDiscount being called with new status.
        // However, the component calls this method.
        // Let's implement a fetch-then-update approach or assume the component passes the NEW status?
        // The component code: `handleToggleStatus` calls `toggleDiscountStatus(id)`. It doesn't pass new status.
        // So we must fetch first.
        // Since we don't have a 'get one' endpoint in the service (only getDiscounts), 
        // we might need to rely on the backend to toggle or fetch all.
        // Let's just use the update endpoint with a specific flag if backend supported it, but it doesn't.
        // Workaround: The component should probably pass the new status.
        // Let's change the component logic or fetch all here.
        // Fetching all is inefficient but safe.
        const discounts = await this.getDiscounts();
        const discount = discounts.find(d => d.id === id);
        if (!discount) throw new Error('Discount not found');

        return this.updateDiscount(id, { is_active: !discount.is_active });
    }

    /**
     * Get usage history for a discount
     * @param {number} id
     * @returns {Promise<Array>}
     */
    async getDiscountUsage() {
        // Backend doesn't have usage history endpoint yet.
        // Return empty array for now.
        return [];
    }

    /**
     * Validate a coupon code
     * @param {string} code
     * @param {number} cartTotal
     * @param {Array} cartItems
     * @returns {Promise<Object>}
     */
    async validateCoupon(code, cartTotal, cartItems) {
        try {
            const response = await api.post('/coupons/validate', {
                code,
                cartTotal,
                cartItems
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Code invalide');
        }
    }
}

export default new DiscountService();
